import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useMediaQuery, ThemeProvider, createTheme } from "@mui/material";
import Sidebar from "./components/Sidebar";
import Login from "./pages/Login";
import Dashboard from "./pages/Dashboard";
import CreateCertificate from "./pages/CreateCertificate";
import ManageCertificates from "./pages/ManageCertificates";
import Templates from "./pages/Templates";
import Settings from "./pages/Settings";
import ReportGenerator from "./pages/ReportGenerator";
import TrainingApp from "./pages/TrainingApp";
import TrainingMatrix from "./pages/TrainingMatrix";
import NotFound from "./pages/NotFound";
import VerifyCertificate from "./pages/VerifyCertificate";
import theme from "./theme";
import "./i18n";
import "./styles/App.css";

const PrivateRoute = ({ children, showSidebar = true }) => {
  const token = localStorage.getItem("token");
  if (!token) {
    return <Navigate to="/login" />;
  }
  return showSidebar ? (
    <div className="private-route-container">
      <Sidebar />
      {children}
    </div>
  ) : (
    children
  );
};

function App() {
  const { i18n, t } = useTranslation();
  const prefersDarkMode = useMediaQuery("(prefers-color-scheme: dark)");
  const currentTheme = prefersDarkMode ? theme.dark : theme.light;

  const muiTheme = createTheme({
    palette: {
      mode: prefersDarkMode ? "dark" : "light",
      background: {
        default: currentTheme.background,
        paper: currentTheme.cardBackground,
      },
      text: {
        primary: currentTheme.textPrimary,
        secondary: currentTheme.textSecondary,
      },
    },
  });

  const changeLanguage = (lng) => {
    i18n.changeLanguage(lng);
  };

  // Define routes where language switcher should not appear
  const noLanguageSwitcherRoutes = ["/login", "/verify", "/report-generator", "/training"];

  return (
    <ThemeProvider theme={muiTheme}>
      <div className="app-container" style={{ backgroundColor: currentTheme.background, color: currentTheme.textPrimary }}>
        <Router>
          {/* Conditionally render Language Switcher based on route */}
          <Routes>
            {noLanguageSwitcherRoutes.map((path) => (
              <Route
                key={path}
                path={path}
                element={null} // No language switcher for these routes
              />
            ))}
            <Route
              path="*"
              element={
                <div className="language-switcher">
                  <button onClick={() => changeLanguage("en")}>{t("language.en")}</button>
                  <button onClick={() => changeLanguage("de")}>{t("language.de")}</button>
                </div>
              }
            />
          </Routes>
          {/* Main Routes */}
          <Routes>
            {/* Public Routes without Sidebar */}
            <Route path="/login" element={<Login />} />
            <Route path="/verify" element={<VerifyCertificate />} />
            <Route path="/report-generator" element={<ReportGenerator />} />
            <Route path="/training" element={<TrainingApp />} />
            <Route path="/" element={<Navigate to="/login" replace />} />
            <Route path="*" element={<NotFound />} />
            {/* Authenticated Routes with Sidebar */}
            <Route
              path="/dashboard"
              element={
                <PrivateRoute>
                  <Dashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/create-certificate"
              element={
                <PrivateRoute>
                  <CreateCertificate />
                </PrivateRoute>
              }
            />
            <Route
              path="/manage-certificates"
              element={
                <PrivateRoute>
                  <ManageCertificates />
                </PrivateRoute>
              }
            />
            <Route
              path="/templates"
              element={
                <PrivateRoute>
                  <Templates />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <Settings />
                </PrivateRoute>
              }
            />
            <Route
              path="/training-matrix"
              element={
                <PrivateRoute>
                  <TrainingMatrix />
                </PrivateRoute>
              }
            />
          </Routes>
        </Router>
      </div>
    </ThemeProvider>
  );
}

export default App;